import { ChevronLeft, Expand, Minimize2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const homePageGradientStyle = {
  background:
    'linear-gradient(135deg, #B3E5FC 0%, rgb(225, 212, 248) 25%, #81D4FA 50%, #C5CAE9 75%, #B39DDB 100%)',
  boxShadow:
    '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
  backdropFilter: 'blur(30px)',
};

const glassCardStyle = {
  borderRadius: '12px',
  border: '1px solid var(--card-11-stroke, #FFF)',
  background:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
  boxShadow:
    '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
  backdropFilter: 'blur(30px)',
};

const CameraAttendanceAllFeedsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fullscreenRootRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFullscreenChange = () => {
      const isThisFullscreen =
        Boolean(document.fullscreenElement) &&
        fullscreenRootRef.current != null &&
        document.fullscreenElement === fullscreenRootRef.current;
      setIsFullscreen(isThisFullscreen);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    onFullscreenChange();
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const feeds = useMemo(() => {
    const candidate = location.state?.feeds;
    return Array.isArray(candidate) && candidate.length ? candidate : [];
  }, [location.state?.feeds]);

  const selectedPreviewCameraId = location.state?.selectedPreviewCameraId || '';
  const fullscreenColumns = 4;
  const fullscreenRows = Math.max(1, Math.ceil(feeds.length / fullscreenColumns));

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && fullscreenRootRef.current) {
        await fullscreenRootRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
    }
  };

  useEffect(() => {
    const shouldAutoFullscreen = location.state?.autoFullscreen === true;
    if (!shouldAutoFullscreen) return;

    const tryEnter = async () => {
      try {
        if (!document.fullscreenElement && fullscreenRootRef.current) {
          await fullscreenRootRef.current.requestFullscreen();
        }
      } catch (error) {
        console.error('Failed to auto-enter fullscreen:', error);
      }
    };

    const timer = window.setTimeout(() => {
      tryEnter();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.state?.autoFullscreen]);

  const exitFullscreenIfNeeded = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Failed to exit fullscreen:', error);
    }
  };

  return (
    <div className="w-full min-h-full flex flex-col gap-4 rounded-xl" style={homePageGradientStyle}>
      <section className="flex items-center justify-between gap-3 px-1 sm:px-2 py-1">
        <button
          type="button"
          onClick={async () => {
            await exitFullscreenIfNeeded();
            navigate(-1);
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-white/60 bg-white/60 px-3 py-2 text-sm font-semibold text-neutral-800 hover:bg-white/80"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        <div className="text-right">
          <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900">All Camera Feeds</h1>
          <p className="text-sm text-neutral-700 mt-1">
            Total feeds: <span className="font-semibold">{feeds.length}</span>
          </p>
        </div>

        <button
          type="button"
          onClick={handleToggleFullscreen}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 text-white !text-white text-sm font-medium hover:bg-green-700 transition-colors"
        >
          {isFullscreen ? (
            <>
              <Minimize2 className="w-4 h-4 text-white" />
              Exit Full Screen
            </>
          ) : (
            <>
              <Expand className="w-4 h-4 text-white" />
              Full Screen
            </>
          )}
        </button>
      </section>

      <section className="relative flex-1 min-h-0 rounded-xl p-3 sm:p-4" style={glassCardStyle}>
        <div
          ref={fullscreenRootRef}
          className={`h-full ${
            isFullscreen
              ? 'w-screen h-screen overflow-hidden rounded-none border-0 bg-neutral-900 p-4'
              : 'overflow-auto rounded-lg border border-cyan-100 bg-cyan-50/60 p-3'
          }`}
        >
          {feeds.length === 0 ? (
            <div className="h-full w-full flex items-center justify-center p-6">
              <p className="text-sm text-neutral-700">No camera feeds available.</p>
            </div>
          ) : (
            <div
              className={`grid gap-1 ${isFullscreen ? 'h-full grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}
              style={
                isFullscreen
                  ? {
                      gridTemplateRows: `repeat(${fullscreenRows}, minmax(0, 1fr))`,
                    }
                  : undefined
              }
            >
              {feeds.map(camera => (
                <button
                  key={camera.id}
                  type="button"
                  onClick={async () => {
                    await exitFullscreenIfNeeded();
                    navigate('/camera-attendance', {
                      state: { selectedPreviewCameraId: camera.id },
                    });
                  }}
                  className={`rounded-xl overflow-hidden border border-white/60 bg-black/80 text-left relative focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                    isFullscreen ? 'h-full' : ''
                  }`}
                >
                  <div className={`relative w-full ${isFullscreen ? 'h-full' : 'aspect-video'}`}>
                    {camera?.image ? (
                      <img
                        src={camera.image}
                        alt={`${camera.name} feed`}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <p className="text-xs text-white/80">No feed URL</p>
                      </div>
                    )}
                    {camera.id === selectedPreviewCameraId ? (
                      <div className="absolute inset-0 ring-2 ring-sky-400" aria-hidden />
                    ) : null}
                    <div className="absolute inset-x-0 top-0 p-2 bg-gradient-to-b from-black/70 to-transparent">
                      <p className="text-xs font-semibold text-white">{camera.name}</p>
                    </div>
                    <div className="absolute bottom-2 left-2 inline-flex items-center gap-2 rounded-full bg-emerald-600/90 px-2.5 py-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-[11px] font-semibold text-white">LIVE</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CameraAttendanceAllFeedsScreen;
